import { store } from '../../firebase/firebaseSetup';
import {
    collection,
    getDoc,
    getDocs,
    doc,
    addDoc,
    setDoc,
    deleteDoc,
    DocumentReference,
    DocumentData,
    CollectionReference,
    query,
    limit,
    orderBy
} from 'firebase/firestore';
import AbstractClass from './abstractClass';
import ErrorInterface from '../interfaces/errorInterface';

export default class AbstractService<S extends AbstractClass> {
    collectionId: string;
    customClass: any;
    queryCollection: CollectionReference<DocumentData>;

    constructor(collectionId: string, customClass: any) {
        this.collectionId = collectionId;
        this.customClass = customClass;
        this.queryCollection = collection(store, this.collectionId);
    }

    private formatToSave(item: Record<string, any>): Record<string, any> {
        return JSON.parse(JSON.stringify(item));
    }

    private getReference = (
        itemId: string
    ): DocumentReference<DocumentData> => {
        return doc(store, this.collectionId, itemId);
    };

    /**
     * Get a document from the collection
     * @param itemId the id of the document
     * @returns Promise<S>
     */
    get = async (itemId: string): Promise<S | ErrorInterface> => {
        try {
            const docSnap = await getDoc(this.getReference(itemId));
            return docSnap.exists()
                ? new this.customClass({ id: docSnap.id, ...docSnap.data() })
                : ({
                      error: true,
                      errorMsg: 'No data found'
                  } as ErrorInterface);
        } catch (e: any) {
            return { error: true, errorMsg: e.message } as ErrorInterface;
        }
    };

    /**
     * Get all documents from the collection
     * @param filter the filter to apply. Not used here
     * @returns Promise<S[]>
     */
    getAll = async (role?: string): Promise<S[] | ErrorInterface> => {
        let results: S[] | ErrorInterface = [];
        try {
            const querySnapshot = await getDocs(this.queryCollection);
            results = querySnapshot.docs.map((item: any) => {
                return new this.customClass({ id: item.id, ...item.data() });
            });
            return results as S[];
        } catch (e: any) {
            console.error('Error getting item: ', e);
            results = { error: true, errorMsg: e.message } as ErrorInterface;
            return results as ErrorInterface;
        }
    };

    /**
     * Get the first documents from the collection
     * @param sorting the parameter to use for the sorting
     * @returns Promise<S[]>
     */
    getFirst = async (
        sorting: string = 'created'
    ): Promise<S | ErrorInterface> => {
        let results: S | ErrorInterface;
        try {
            const querySnapshot = await getDocs(
                query(this.queryCollection, limit(1), orderBy(sorting, 'asc'))
            );
            results = querySnapshot.docs.map((item: any) => {
                return new this.customClass({ id: item.id, ...item.data() });
            })[0];
            return results as S;
        } catch (e: any) {
            console.error('Error getting item: ', e);
            results = { error: true, errorMsg: e.message } as ErrorInterface;
            return results as ErrorInterface;
        }
    };

    /**
     * Create a new document in the collection
     * @param item the item to be added
     * @returns Promise<S[]>
     */
    create = async (item: S): Promise<S | ErrorInterface> => {
        let result: S | ErrorInterface;
        try {
            const json = item.toJSON();
            const dataToSave = this.formatToSave(json.data);
            const response = (await addDoc(
                this.queryCollection,
                dataToSave
            )) as unknown as S;
            result = new this.customClass({ id: response.id, ...dataToSave });
        } catch (e: any) {
            console.error('Error editing item: ', e);
            result = { error: true, errorMsg: e.message } as ErrorInterface;
        }
        return result;
    };

    /**
     * Update a document in the collection
     * @param item the item to be updated
     * @returns Promise<S>
     */
    update = async (item: S): Promise<S | ErrorInterface> => {
        let result: S | ErrorInterface;
        try {
            const json = item.toJSON();
            const dataToSave = this.formatToSave(json.data);
            const docRef = this.getReference(json.id);
            (await setDoc(docRef, dataToSave)) as unknown as S;
            result = new this.customClass(item);
        } catch (e: any) {
            console.error('Error editing item: ', e);
            result = { error: true, errorMsg: e.message } as ErrorInterface;
        }
        return result;
    };

    /**
     * Create or update a new document in the collection
     * @param item the item to be added or updated
     * @returns Promise<S>
     */
    save = async (item: S): Promise<S | ErrorInterface> => {
        return item.id ? this.update(item) : this.create(item);
    };

    /**
     * Delete a document from the collection
     * @param id the id of the document to be deleted
     * @returns Promise<S>
     */
    delete = async (id: string): Promise<void | ErrorInterface> => {
        let result: void | ErrorInterface;
        try {
            const docRef = doc(store, this.collectionId, id);
            (await deleteDoc(docRef)) as unknown as S;
        } catch (e: any) {
            console.error('Error deleting item: ', e);
            result = { error: true, errorMsg: e.message } as ErrorInterface;
        }
        return result;
    };
}
